import { memo, type KeyboardEvent } from 'react'
import { ChevronRightIcon } from '@quickmd-llc/design-system'
import type { ApplicationSettingsPerStateAdminApiContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { getStateByAbbreviation } from '@/utils/common'

type StateSettingsRowProps = {
  StateCode?: ApplicationSettingsPerStateAdminApiContract['StateCode'] | undefined
  IsPhoneVisitCommunicationTypeAllowed?:
    | ApplicationSettingsPerStateAdminApiContract['IsPhoneVisitCommunicationTypeAllowed']
    | undefined
  DrugScreenHtml?: ApplicationSettingsPerStateAdminApiContract['DrugScreenHtml'] | undefined
  onSelectState: (stateCode: string) => void
}

const StateSettingsRow = memo(
  ({ StateCode, IsPhoneVisitCommunicationTypeAllowed, DrugScreenHtml, onSelectState }: StateSettingsRowProps) => {
    const handleClick = () => onSelectState(StateCode ?? '')
    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onSelectState(StateCode ?? '')
      }
    }

    return (
      <div
        className='flex cursor-pointer items-center gap-4 border-b border-shade-10 bg-white px-6 py-4 last:rounded-b-large last:border-b-0 hover:bg-[#f2fafb]'
        /* hover teal tint: linear-gradient(0deg, rgba(0,162,178,0.05), rgba(0,162,178,0.05)), #ffffff ≈ #f2fafb — no exact DS token */
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role='button'
        tabIndex={0}
        data-testid='pw_state-settings-row'>
        {/* State column — holds 240px, shrinks to a floor on narrow viewports (matches header) */}
        <div className='flex w-[240px] min-w-[120px] items-center overflow-hidden'>
          <span className='overflow-hidden text-ellipsis whitespace-nowrap text-sm font-semibold leading-5 text-text-dark'>
            {getStateByAbbreviation(StateCode ?? '')}
          </span>
        </div>

        {/* Drug screening column — holds 200px, shrinks to a floor on narrow viewports (matches header) */}
        <div className='flex w-[200px] min-w-[120px] items-center overflow-hidden'>
          <span className='whitespace-nowrap text-sm font-normal leading-5 text-text-light'>
            {DrugScreenHtml ? 'Required' : 'Not required'}
          </span>
        </div>

        {/* Phone visits column — flex: 1 1 0 */}
        <div className='flex min-w-0 flex-1 items-center overflow-hidden'>
          <span className='whitespace-nowrap text-sm font-normal leading-5 text-text-light'>
            {IsPhoneVisitCommunicationTypeAllowed ? 'Available' : 'Not available'}
          </span>
        </div>

        {/* Arrow column — flex: 0 0 24px */}
        <div className='flex w-6 shrink-0 items-center justify-end'>
          <ChevronRightIcon iconSize='s' className='text-text-light' />
        </div>
      </div>
    )
  },
)

StateSettingsRow.displayName = 'StateSettingsRow'

export default StateSettingsRow
